import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const texts = {
  title: "Colaboración",
  heroImage: "https://net-basis.net/Bilder/netbasis-partnership-dach-market-entry.webp",
  heroAlt: "Un empresario ofrece la mano para una alianza de confianza y la entrada en el mercado DACH.",
  heroClaimLine1: "¡Fuertes gracias",
  heroClaimLine2: "a la alianza!",
  h1Text: "Su vínculo de confianza para el éxito comercial internacional. Superamos los obstáculos culturales, lingüísticos y logísticos para establecer a los fabricantes extranjeros de TIC de manera segura y eficiente en la región DACH.",
  introParagraphs: [
    "En el mismo instante en que palabras como \"internacional\" o \"extranjero\" aparecen en sus reflexiones y actividades comerciales, los procesos derivados suelen volverse más complejos. Se deben tener en cuenta innumerables factores: barreras lingüísticas, situaciones legales confusas o incluso diferencias horarias significativas pueden surgir e interferir en las planificaciones establecidas. Por último, pero no menos importante, la dificultad de estar presente personalmente sobre el terreno para seguir lo que realmente ocurre representa un obstáculo importante para muchos fabricantes.",
    "Precisamente en este punto, NetBasis International es el socio ideal a su lado. Como empresa de consultoría, importación y comercio de larga trayectoria y gran experiencia, actuamos como su sólido nexo local. Intervenimos como su mediador cualificado sobre el terreno y le protegemos eficazmente de errores críticos o fatales para su negocio que pueden surgir al entrar en el mercado internacional.",
    "Nuestros fabricantes socios internacionales se benefician directamente de nuestro profundo conocimiento del mercado y de nuestra sólida presencia en toda la región económica de habla alemana. Como su importador operativo, le ponemos en contacto directo con todas las empresas de ventas y organizaciones comerciales locales pertinentes en un solo paso. Con NetBasis International y los contactos de alto nivel asociados a nosotros, el lanzamiento de productos sobresalientes y soluciones TIC de futuro se vuelve significativamente más sencillo y eficiente.",
    "Al mismo tiempo, nuestros socios de distribución locales aprecian colaborar con una empresa con sede en Alemania como NetBasis International. Esto les otorga un acceso inmediato y sencillo a una amplia cartera de productos y soluciones innovadoras de diversos fabricantes extranjeros. Estamos orgullosos de colaborar ya con éxito con un gran número de fabricantes destacados que no compiten entre sí.",
  ],
  experienceHeading: "Un gran bagaje de experiencia",
  experienceParagraphs: [
    "Los mercados cambian — el conocimiento, los contactos y la experiencia permanecen.",
    "Durante casi dos décadas, NetBasis International ha impulsado con éxito el desarrollo de fabricantes extranjeros y marcas tecnológicas en la región DACH, estableciéndolos de manera sostenible en el mercado y brindando un respaldo a largo plazo. Para nosotros, nunca se trata de ventas puras. Es una cuestión de confianza genuina y de una comprensión profunda de ambas partes: la marca global que desea consolidarse en un nuevo mercado y el cliente local que busca un socio fiable.",
    "Las relaciones de confianza y fiables con nuestros socios no son un simple beneficio secundario, son el fundamento mismo de un éxito duradero y de clientes satisfechos.",
    "Los fabricantes van y vienen, pero nuestra capacidad para analizar con precisión los mercados y crear las conexiones correctas permanece inalterable. A continuación, verá un extracto de asociaciones importantes forjadas a lo largo de estos casi veinte últimos años.",
  ],
  manufacturers: [
    {
      name: "LeadDoubler ApS",
      logo: "https://net-basis.net/Bilder/Lead-Doubler-logo.png",
      desc: "Fabricante danés de software de generación de leads",
    },
    {
      name: "Mondago Ltd",
      logo: "https://net-basis.net/Bilder/Mondago-logo.png",
      desc: "Fabricante inglés de soluciones CTI",
    },
    {
      name: "Pinewood",
      logo: "https://net-basis.net/Bilder/Pinewood-logo.jpg",
      desc: "Fabricante belga de soluciones multimedia",
    },
    {
      name: "Retell Call Recording",
      logo: "https://net-basis.net/Bilder/Re-Tell-logo.png",
      desc: "Fabricante inglés de soluciones de grabación de llamadas",
    },
    {
      name: "Rohde & Schwarz Topex",
      logo: "https://net-basis.net/Bilder/Rohde-Schwarz-logo.jpg",
      desc: "Fabricante rumano de pasarelas GSM",
    },
    {
      name: "Samsung",
      logo: "https://net-basis.net/Bilder/Samsung-logo.png",
      desc: "Colaboración en el área de su solución CTI",
    },
    {
      name: "Voice gear",
      logo: "https://net-basis.net/Bilder/Voice-Gear-logo.jpg",
      desc: "Marca comercial internacional de soluciones de audio",
    },
    {
      name: "Zenion",
      logo: "https://net-basis.net/Bilder/Zenion-logo.jpg",
      desc: "Marca comercial internacional de mini-PCs fanless",
    },
  ],
  claim: "NetBasis International - drive to excellence with excellent drivers !",
};

const renderWithBold = (text: string) => {
  const parts = text ? text.split(COMPANY_NAME) : [];
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <strong className="text-primary-600">{COMPANY_NAME}</strong>
      )}
    </span>
  ));
};

export default function ColaboracionPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Colaboración | NetBasis International"
        description="Colaboraciones B2B consolidadas y un gran bagaje de experiencia en la región DACH. Creamos las conexiones correctas para un éxito duradero."
        ogUrl="https://net-basis.net/es/colaboracion"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={texts.heroImage}
                alt={texts.heroAlt}
                title={texts.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex flex-col items-end justify-center px-6 md:px-10 pointer-events-none">
                <div className="flex flex-col items-end -translate-y-14 md:-translate-y-28">
                  <p
                    className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right animate-[bdTextSlide_12s_ease-in-out_infinite]"
                    style={{
                      fontFamily: '"Open Sans", sans-serif',
                      paddingRight: '0.5rem',
                    }}
                  >
                    <span className="block">{texts.heroClaimLine1}</span>
                    <span className="block">{texts.heroClaimLine2}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* WIDGET 1 - Überschrift (jetzt h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.title}
            </h2>

            {/* WIDGET 2 - H1: fetter Fließtext */}
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {texts.h1Text}
            </h1>

            {/* WIDGET 3 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.introParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={idx}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* WIDGET 4 - Überschrift (h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {texts.experienceHeading}
            </h2>

            {/* WIDGET 5 - normaler Text */}
            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              {texts.experienceParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={`exp-${idx}`}>{renderWithBold(text)}</p>
                  )
              )}
            </div>

            {/* Partner-Logos - UNVERÄNDERT */}
            <div className="mt-8 md:mt-10 space-y-6 md:space-y-8">
              {texts.manufacturers.map((m, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-6 md:gap-10"
                >
                  <div className="overflow-hidden w-36 h-24 sm:w-40 sm:h-28 md:w-48 md:h-32 flex-shrink-0 flex items-center justify-center">
                    <img loading="lazy"
                      src={m.logo}
                      alt=""
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-sm sm:text-lg md:text-xl font-bold text-primary-600 mb-1">
                      {m.name}
                    </h3>
                    <p className="text-xs sm:text-sm md:text-base text-foreground-950 leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {texts.claim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}